import {
	type UpdateRecordRequest,
	type UpdateWorkflowDbSchemaRequest,
	useDeleteRecord,
	useGetWorkflowDbData,
	useGetWorkflowDbSchema,
	useReadRecord,
	useUpdateRecord,
	useUpdateWorkflowDbSchema,
	useWriteRecord,
	useWorkflowDbStore,
} from "@/features/workflow-db";
import {
	useDeleteWorkflowUpload,
	useGetDownloadUrl,
	useGetWorkflowUploads,
	useUploadWorkflowFile,
	useWorkflowUploadStore,
} from "@/features/workflow-upload";
import { HStack, VStack } from "@/shared/ui/primitives";
import { useNavigate } from "@tanstack/react-router";
import * as lucideReact from "lucide-react";
import { Fragment, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { RuntimeModules } from "./dashboard-runtime.compiler";
import * as shadcn from "./dashboard-runtime.shadcn";

export const useDashboardRuntimeModules = (workflowId: string): RuntimeModules => {
	const useDBData = useCallback((options?: { polling?: boolean }) => {
		const schemaQuery = useGetWorkflowDbSchema(workflowId);
		const dataQuery = useGetWorkflowDbData(workflowId, options);
		const data = useWorkflowDbStore((state) => state.data);
		const schema = useWorkflowDbStore((state) => state.schema);

		return {
			...dataQuery,
			data,
			schema,
			isReady: schemaQuery.isSuccess && dataQuery.isSuccess,
			isError: schemaQuery.isError || dataQuery.isError,
			error: schemaQuery.error || dataQuery.error,
			schemaQuery,
		};
	}, [workflowId]);

	const useDBSchema = useCallback(() => {
		const query = useGetWorkflowDbSchema(workflowId);
		const schema = useWorkflowDbStore((state) => state.schema);

		return {
			...query,
			schema,
		};
	}, [workflowId]);

	const useDBUpdate = useCallback(() => {
		const updateMutation = useUpdateRecord();
		const writeMutation = useWriteRecord();
		const deleteMutation = useDeleteRecord();

		return {
			...updateMutation,
			update: (request: UpdateRecordRequest) =>
				updateMutation.mutate({ workflowId, request }),
			updateAsync: (request: UpdateRecordRequest) =>
				updateMutation.mutateAsync({ workflowId, request }),
			write: (table: string, data: Record<string, unknown>) =>
				writeMutation.mutate({ workflowId, table, data }),
			writeAsync: (table: string, data: Record<string, unknown>) =>
				writeMutation.mutateAsync({ workflowId, table, data }),
			delete: (table: string, ref: string) =>
				deleteMutation.mutate({ workflowId, table, ref }),
			deleteAsync: (table: string, ref: string) =>
				deleteMutation.mutateAsync({ workflowId, table, ref }),
		};
	}, [workflowId]);

	const useDBSchemaUpdate = useCallback(() => {
		const mutation = useUpdateWorkflowDbSchema();

		return {
			...mutation,
			update: (request: UpdateWorkflowDbSchemaRequest) => mutation.mutate({ workflowId, request }),
			updateAsync: (request: UpdateWorkflowDbSchemaRequest) =>
				mutation.mutateAsync({ workflowId, request }),
		};
	}, [workflowId]);

	const useDBRead = useCallback(() => {
		const mutation = useReadRecord();

		return {
			...mutation,
			read: (ref: string, select?: string[]) =>
				mutation.mutateAsync({ workflowId, ref, select }),
		};
	}, [workflowId]);

	const useBackRoute = useCallback(() => {
		const navigate = useNavigate();
		return () => navigate({ to: "/workflow/$id", params: { id: workflowId } });
	}, [workflowId]);

	const useUploads = useCallback(() => {
		const query = useGetWorkflowUploads(workflowId);
		const uploads = useWorkflowUploadStore((state) => state.uploads);

		return {
			...query,
			uploads,
		};
	}, [workflowId]);

	const useUpload = useCallback(() => {
		const uploadMutation = useUploadWorkflowFile();
		const deleteMutation = useDeleteWorkflowUpload();
		const downloadMutation = useGetDownloadUrl();

		return {
			upload: (file: File, tag?: string) =>
				uploadMutation.mutate({ workflowId, file, tag }),
			uploadAsync: (file: File, tag?: string) =>
				uploadMutation.mutateAsync({ workflowId, file, tag }),
			delete: (uploadId: string) =>
				deleteMutation.mutate({ workflowId, uploadId }),
			deleteAsync: (uploadId: string) =>
				deleteMutation.mutateAsync({ workflowId, uploadId }),
			getDownloadUrl: (uploadId: string) =>
				downloadMutation.mutateAsync({ workflowId, uploadId }),
			isUploading: uploadMutation.isPending,
			isDeleting: deleteMutation.isPending,
		};
	}, [workflowId]);

	return useMemo(
		() => ({
			react: {
				useState,
				useEffect,
				useCallback,
				useRef,
				useMemo,
				useReducer,
				useContext,
				Fragment,
			},
			hooks: {
				useDBData,
				useDBSchema,
				useDBRead,
				useDBUpdate,
				useDBSchemaUpdate,
				useWorkflowDbStore,
				useBackRoute,
				useUploads,
				useUpload,
			},
			components: {
				...shadcn,
				HStack,
				VStack,
			},
			icons: {
				...lucideReact,
			},
			utils: {
				Math,
			},
		}),
		[useDBData, useDBSchema, useDBRead, useDBSchemaUpdate, useDBUpdate, useBackRoute, useUploads, useUpload],
	);
};

/** @deprecated Use `useDashboardRuntimeModules` instead */
export const useDashboardRuntimeRegistry = useDashboardRuntimeModules;
